/* eslint-disable no-use-before-define */
/* eslint-disable no-unused-expressions */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import WebChartReact from '../wcr-core'
import { useChart } from './hooks'
import { resizeAxis } from './utils'
import { axisOptions, colorsOptions, highchartsOptions } from './options'
import Days from './logics/Days'
import Minutes from './logics/Minutes'

import useCustomSma from './hooks/useCustomSma'
import useHighLowPoints from './hooks/useHighLowPoints'
import useChartOptions from "../../hooks/useChartOptions";

const Chart = (props) => {
  const { period, unit, reflow, indicators, chartRef, stockPrice } = props

  const chart = useChart(chartRef)
  const isDrawSection = useRef(false)
  const { option } = useChartOptions()

  const { render } = useHighLowPoints()
  const { start, removeAll } = useCustomSma(chart, isDrawSection)
  const [updateDivLines, setUpdateDivLines] = useState()
  const [daysLogic, setDaysLogic] = useState()
  const [minutesLogic, setMinutesLogic] = useState()

  const customSma = document.querySelector('.tools-container .custom-sma')

  const globalOptions = useMemo(
    () => ({
      ...props.globalOptions,
      chart: {
        events: {
          click: start,
          render,
        },
      },
    }),
    [props.globalOptions, start, render]
  )

  useEffect(() => {
    if (!customSma) return

    const makeCustomSma = () => {
      isDrawSection.current = !isDrawSection.current
    }

    customSma.addEventListener('click', makeCustomSma)

    return () => {
      customSma.removeEventListener('click', makeCustomSma)
    }
  }, [customSma])

  useEffect(() => {
    if (option.customSeries !== 'drawSma') {
      isDrawSection.current = false
    }
    if (option.customSeries && option.customSeries !== 'average') {
      document.querySelector('.highcharts-toggle-annotations')?.click()
    }

    removeAll()

    daysLogic?.hide()
    minutesLogic?.hide()

    if (option.customSeries === 'buySell') {
      if (period === 'days') {
        daysLogic?.draw()
      } else if (period === 'minutes' && Number(unit) === 30) {
        minutesLogic?.draw()
      }
    }

    chart?.redraw()
  }, [option, chart])

  useEffect(() => {
    if (!chart) return

    setDaysLogic(new Days(chart))
    setMinutesLogic(new Minutes(chart))

    chart.get('main').update(
      {
        zIndex: 2,
        lastVisiblePrice: {
          enabled: true,
          label: {
            enabled: true,
            format: '{value:,.0f}',
          },
        },
      },
      false
    )

    chart.get('main-yaxis').update(
      {
        height: '80%',
        gridLineWidth: 1,
        crosshair: {
          label: {
            enabled: false,
          },
        },
      },
      false
    )

    chart.get('volume-yaxis').update(
      {
        top: '80%',
        height: '20%',
        gridLineWidth: 1,
        title: {
          ...axisOptions.yAxisTitle({
            text: '거래량',
          }),
        },
        showFirstLabel: false,
      },
      false
    )

    chart.redraw()
    updateIndicators()
  }, [chart])

  useEffect(() => {
    if (!chart) return

    chart.reflow()
  }, [chart, reflow])

  useEffect(() => {
    updateIndicators()
  }, [JSON.stringify(indicators)])

  return (
    <WebChartReact
      {...props}
      ref={chartRef}
      globalOptions={globalOptions}
      highchartsOptions={highchartsOptions}
      colors={colorsOptions.candlestick}
      initCandlesHandler={initCandlesHandler}
      updateCandlesHandler={updateCandlesHandler}
      updateDivLines={updateDivLines}
      removeCustomBandId={() => {
        isDrawSection.current = false
        removeAll()
      }}
      xAxisGridLine={stockPrice}
    />
  )

  function updateIndicators() {
    if (!chart) return

    // SMA
    if (Array.isArray(indicators.sma)) {
      indicators.sma.forEach((smaOptions) => {
        if (!smaOptions.period) return

        const lineWidth = parseInt(smaOptions.lineWidth, 10) || 1

        const sma = chart.get(`sma-main-${smaOptions.period}`)
        if (sma.userOptions.lineWidth !== lineWidth) {
          sma.update(
            {
              lineWidth: lineWidth,
            },
            false
          )
        }

        if (sma.visible !== smaOptions.visible) {
          sma.update(
            {
              visible: !!smaOptions.visible,
            },
            false
          )
        }

        if (sma.color !== smaOptions.visible) {
          sma.update(
            {
              color: smaOptions.color,
            },
            false
          )
        }
      })
    }

    // VSMA
    if (Array.isArray(indicators.vsma)) {
      indicators.vsma.forEach((vsmaOptions) => {
        if (!vsmaOptions.period) return

        const lineWidth = parseInt(vsmaOptions.lineWidth, 10) || 1

        const vsma = chart.get(`vsma-volume-${vsmaOptions.period}`)
        if (vsma.userOptions.lineWidth !== lineWidth) {
          vsma.update(
            {
              lineWidth: lineWidth,
            },
            false
          )
        }

        if (vsma.visible !== vsmaOptions.visible) {
          vsma.update(
            {
              visible: !!vsmaOptions.visible,
            },
            false
          )
        }

        if (vsma.color !== vsmaOptions.color) {
          vsma.update(
            {
              color: vsmaOptions.color,
            },
            false
          )
        }
      })
    }

    // Volume
    if (indicators.volume) {
      const visible = !!indicators.volume.visible
      const volumeSeries = chart.get('volume')
      const volumeYAxis = chart.get('volume-yaxis')

      volumeSeries.update(
        {
          visible: visible,
        },
        false
      )

      volumeYAxis.update(
        {
          visible: visible,
        },
        false
      )
    }

    chart.series.forEach((series) => {
      if (
        series === undefined ||
        series.userOptions === undefined ||
        series.userOptions.type !== 'sma'
      ) {
        return
      }

      series.update(false)
    })

    resizeAxis(chart)
    setUpdateDivLines(new Date().getTime())
  }

  function initCandlesHandler(candles) {
    chart.get('volume').setData(
      candles.map((candle, i) => ({
        x: i,
        y: candle.candleAccTradeVolume,
        color:
          i > 0
            ? candles[i - 1].candleAccTradeVolume > candle.candleAccTradeVolume
              ? colorsOptions.candlestick.fall
              : colorsOptions.candlestick.rise
            : colorsOptions.candlestick.rise,
      })),
      false
    )

    chart.series.forEach((series) => {
      if (
        series === undefined ||
        series.userOptions === undefined ||
        !['sma', 'vbp'].includes(series.userOptions.type)
      ) {
        return
      }

      series.update(series.userOptions, false)
    })

    setTimeout(() => {
      daysLogic.hide()
      minutesLogic.hide()

      if (option.customSeries === 'buySell') {
        if (period === 'days') {
          daysLogic.draw()
        } else if (period === 'minutes' && Number(unit) === 30) {
          minutesLogic.draw()
        }
      }

      chart.redraw()
    }, 0)
  }

  function updateCandlesHandler(addedCount, candles) {
    chart.get('volume').setData(
      candles.map((candle, i) => ({
        x: i,
        y: candle.candleAccTradeVolume,
        color:
          i > 0
            ? candles[i - 1].candleAccTradeVolume > candle.candleAccTradeVolume
              ? colorsOptions.candlestick.fall
              : colorsOptions.candlestick.rise
            : colorsOptions.candlestick.rise,
      })),
      false
    )

    chart.series.forEach((series) => {
      if (
        series === undefined ||
        series.userOptions === undefined ||
        series.userOptions.type !== 'sma'
      ) {
        return
      }

      series.update()
    })

    if (option.customSeries === 'buySell') {
      if (period === 'days') {
        daysLogic.draw()
      } else if (period === 'minutes' && unit === 30) {
        minutesLogic.draw()
      }
    }

    chart.redraw()
  }
}

Chart.propTypes = {
  code: PropTypes.string.isRequired,
  period: PropTypes.string,
  unit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  quote: PropTypes.object,
  isMobile: PropTypes.bool,
  stockPrice: PropTypes.number,
  isExpand: PropTypes.bool,

  chartRef: PropTypes.any,
  globalOptions: PropTypes.any,
  realtime: PropTypes.any,
  reflow: PropTypes.any,
  indicators: PropTypes.any,
  availablePeriods: PropTypes.any,
  availableMinuteUnits: PropTypes.any,
  api: PropTypes.any,
}

export default Chart
